/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import type { MutableRef } from 'preact/hooks';
import type { Keys } from '#utils/UNSAFE_keys';
import type { ItemActionDetail, SelectionDetail } from '#UNSAFE_Collection';
import type { CollectionData } from '#PRIVATE_TableCommon/Common.types';
import type { ListSelectionMode } from '../List.types';
import type { ActiveCell, ActiveCellDetail } from '../List';

import { useCallback, useRef } from 'preact/hooks';
import { keyExtractor } from '#utils/PRIVATE_collectionUtils';
import { getIsSelectionPending } from '#utils/PRIVATE_textSelectionUtils';
import { handleRowActionGesture } from '#PRIVATE_TableCommon/utils/ActionUtils';
import { getRowKey } from '#PRIVATE_TableCommon/utils/DomUtils';
import { isLogicalCtrl } from '#PRIVATE_TableCommon/utils/EventUtils';
import { getActionContext } from '../utils/ListActionUtils';
import {
  getElementFromCell,
  getFirstFocusableCell,
  LIST_ROW_SELECTOR,
  logicalCellExtractor
} from '../utils/ListDomUtils';
import {
  handleRangeSelectionGesture,
  handleSelectionGesture,
  isRangeSelectionGesture
} from '../utils/ListSelectionUtils';

type PointerInfo = {
  target: EventTarget | null;
  x: number;
  y: number;
};

type UsePointerHandlingOptions<K, D> = {
  rootRef: RefObject<HTMLDivElement>;
  listId: string;
  listData: CollectionData<K, D>[];
  selectionExtensionElementRef: MutableRef<HTMLElement | undefined>;
  activeCell?: ActiveCell<K>;
  onActiveCellChanged: (
    detail: ActiveCellDetail<K>,
    isAutoFocus?: boolean,
    isSelectionExtension?: boolean,
    skipScrollCellIntoView?: boolean
  ) => void;
  isDropDownSelection: boolean;
  selectionMode?: ListSelectionMode;
  selectedKeys?: Keys<K>;
  onListSelectionChange?: (detail: SelectionDetail<K>, isSelectionExtension: boolean) => void;
  onItemAction?: (detail: ItemActionDetail<K, D>) => void;
};

/**
 * Hook that manages pointer interactions on the Preact TableList
 */
export function usePointerHandling<K extends string | number, D>({
  rootRef,
  listId,
  listData,
  selectionExtensionElementRef,
  activeCell,
  onActiveCellChanged,
  isDropDownSelection,
  selectionMode,
  selectedKeys,
  onListSelectionChange,
  onItemAction
}: UsePointerHandlingOptions<K, D>) {
  // tracks whether the most recent 'pointerdown' event was due to a touch gesture
  const selectionTouchRef = useRef<boolean>(false);

  // tracks the current 'pointer over' element for comparison during 'click' event handling to
  // prevent screen readers from triggering undesired selection gestures from simulated events
  const pointerInfoRef = useRef<PointerInfo>();

  // Pointer move handler for the outer List.
  const _pointerMoveHandler = useCallback(
    (event: PointerEvent) => {
      const rootElement = rootRef.current;
      if (rootElement != null) {
        pointerInfoRef.current = { target: event.target, x: event.clientX, y: event.clientY };
      }
    },
    [rootRef]
  );

  // Pointer over handler for the outer List.
  const _pointerOverHandler = useCallback(
    (event: PointerEvent) => {
      const rootElement = rootRef.current;
      if (rootElement != null) {
        pointerInfoRef.current = { target: event.target, x: event.clientX, y: event.clientY };
      }
    },
    [rootRef]
  );

  // Pointer down handler for the outer List. It ensures the area targeted is 'current'.
  // NOTE - This event handling is triggered prior to 'onFocus' handling and ensures the
  // 'pending current' area is set when an initial pointer interaction focuses the List.
  const _pointerDownHandler = useCallback(
    (event: PointerEvent) => {
      const rootElement = rootRef.current;
      if (rootElement != null) {
        selectionTouchRef.current = event.pointerType === 'touch';
        const targetElement = event.target as HTMLElement;
        if (
          onListSelectionChange != null &&
          activeCell != null &&
          event.shiftKey &&
          isRangeSelectionGesture(rootElement, listId, targetElement, activeCell, selectionMode)
        ) {
          // prevent focus change on selection extension gesture
          if (!getIsSelectionPending()) {
            event.preventDefault();
          }
        } else {
          const cell = logicalCellExtractor<K>(targetElement, listId);
          if (
            cell == null ||
            (cell.type === 'data' &&
              keyExtractor<K>(targetElement, `[${LIST_ROW_SELECTOR}='${listId}']`) == null)
          ) {
            // prevent focus shift when interacting with 'non-focusable' regions (ie - scrollbar)
            event.preventDefault();
          }
        }
      }
    },
    [rootRef, listId, activeCell, selectionMode, onListSelectionChange]
  );

  // Pointer leave handler for the outer List.
  const _pointerLeaveHandler = useCallback(() => {
    pointerInfoRef.current = undefined;
  }, []);

  // Click handler for the outer List. It ensures the List's selection state is
  // updated to reflect the area targeted, and any action events are fired correctly.
  const _clickHandler = useCallback(
    (event: PointerEvent) => {
      const rootElement = rootRef.current;
      if (rootElement != null) {
        // only handle click events that match the latest mouse over/move info to prevent screen readers
        // from triggering undesired selections due to simulated click events when transferring focus
        if (
          selectionTouchRef.current ||
          (pointerInfoRef.current != null &&
            pointerInfoRef.current.target === event.target &&
            Math.abs(pointerInfoRef.current.x - event.clientX) <= 1 &&
            Math.abs(pointerInfoRef.current.y - event.clientY) <= 1)
        ) {
          let isSelectionExtension = false;
          const target = event.target as HTMLElement;
          if (onListSelectionChange != null && !getIsSelectionPending()) {
            if (activeCell != null && event.shiftKey) {
              isSelectionExtension = handleRangeSelectionGesture(
                rootElement,
                listId,
                target,
                activeCell,
                onActiveCellChanged,
                selectionExtensionElementRef,
                onListSelectionChange,
                isDropDownSelection,
                selectionMode,
                selectedKeys
              );
            }
            if (!isSelectionExtension) {
              handleSelectionGesture(
                listId,
                target,
                isLogicalCtrl(event) || selectionTouchRef.current,
                onListSelectionChange,
                isDropDownSelection,
                selectionMode,
                selectedKeys
              );
            }
          }
          if (
            onItemAction != null &&
            !getIsSelectionPending() &&
            handleRowActionGesture(
              listId,
              listData,
              onItemAction,
              target,
              LIST_ROW_SELECTOR,
              getActionContext
            )
          ) {
            event.stopPropagation();
          }
          // if click event occurs on the row element itself, focus the first child grid cell
          const isToggleSelectionMode = isDropDownSelection || selectionMode === 'multipleToggle';
          if ((!isSelectionExtension || isToggleSelectionMode) && getRowKey(target) != null) {
            const logicalCell = getFirstFocusableCell<K>(target, listId);
            if (logicalCell != null) {
              onActiveCellChanged({ value: logicalCell }, false, true);
              getElementFromCell(rootElement, listId, logicalCell)?.focus({ preventScroll: true });
            }
          }
        }
      }
    },
    [
      rootRef,
      listId,
      listData,
      selectionExtensionElementRef,
      activeCell,
      onActiveCellChanged,
      isDropDownSelection,
      selectionMode,
      selectedKeys,
      onListSelectionChange,
      onItemAction
    ]
  );

  return {
    pointerHandlingProps: {
      onPointerOver: _pointerOverHandler,
      onPointerMove: _pointerMoveHandler,
      onPointerDown: _pointerDownHandler,
      onPointerLeave: _pointerLeaveHandler,
      onClick: _clickHandler
    }
  };
}
